import {useSuspenseQuery} from '@tanstack/react-query';
import {diApiEu} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import usePendingHandler from '@shared/usePendingHandler';
import useApiError from '@di-shared/features/common/useApiError';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {WebviewUtils} from '@shared/webviewUtils';

export interface SafeDrivingTermsContentsEuProps {
  vin: string;
}

function SafeDrivingTermsContentsEu({vin}: SafeDrivingTermsContentsEuProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const language = useEnvironmentStore((state) => state.lang);

  const {data: termsData} = useSuspenseQuery({
    queryKey: [diApiEu.termsContents.supportedSelectiveTermsContents({termscode: 'SAFETY_SCORE'})],
    queryFn: () => diApiEu.termsContents.supportedSelectiveTermsContents({termscode: 'SAFETY_SCORE'}),
    select: ({data}) => data,
  });

  const [submitAgreeTerms] = usePendingHandler(
    async () => {
      await diApiEu.vehicle.consentSelectiveTermsCode({
        vin,
        termscode: 'SAFETY_SCORE',
        agree: true,
      });
      backOrClose();
    },
    async (error) => {
      await resolveError(error);
    }
  );

  function linkToNotice(language: string) {
    WebviewUtils.openNewWebView({
      url: `${import.meta.env.VITE_NOTICE_EU_URL}?lang=${language}`,
      isModal: true,
      isNavigationBar: false,
      isToolBar: true,
    });
  }

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{termsData.title}</h1>
      </Header>
      <main
        id="content"
        className="content "
      >
        <section className="section ">
          <p className="body_xlarge_r">{termsData.content}</p>
        </section>
      </main>

      <div className="floating_action_area floating_action_area_complex">
        <button
          type="button"
          className="btn btn_large btn_outline"
          onClick={() => backOrClose()}
        >
          {t('ctn_dss_LN_cancel')}
        </button>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            submitAgreeTerms();
            emitEventLog({
              event: 'driving_insight_interaction',
              form_step: 'score enable complete',
              click_text: t('msg_privacy_notice'),
              click_location: 'body',
            });
          }}
        >
          {t('ctn_dss_LN_proceed')}
        </button>
        <div className="floating_link">
          <a
            href="#"
            className="link"
            onClick={(e) => {
              e.preventDefault();
              linkToNotice(language);
            }}
          >
            {t('msg_privacy_notice')}
          </a>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingTermsContentsEuLoading() {
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title skeleton">Driving Safety Score Terms of Use</h1>
      </Header>
      <main
        id="content"
        className="content "
      >
        <section className="section ">
          <p className="body_xlarge_r skeleton">
            By activating the Driving Safety Score service, you will receive a Driving Safety Score in the Kia App based on your driving
            history. We analyze your driving behavior over the duration of each trip, including acceleration and braking patterns, average
            and top speed, and driving hours. For this service, we have engaged LexisNexis Risk Solutions Limited (LNRSE) for its data
            analysis expertise. All data shared with LNRSE is pseudonymized for privacy protection. (For more information about how we
            handle your personal data, see the Kia Connect Privacy Policy [LINK].) Note that if you have shared your car with others, the
            Driving Safety Score will reflect trips made by all drivers and their combined driving behavior. Drivers who share your car can
            also view your Driving Safety Score information. You can disable this service anytime in the Kia App to stop the collection of
            data. No score will be provided while the service is disabled. By pressing [Proceed], you agree to provide data on your driving
            history and behavior to generate a Driving Safety Score for your car.
          </p>
        </section>
      </main>
    </div>
  );
}

SafeDrivingTermsContentsEu.Loading = SafeDrivingTermsContentsEuLoading;

export default SafeDrivingTermsContentsEu;
